import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '@/lib/utils';

/**
 * Base shape for a read-only status pill — `StatusBadge` (customer
 * Active/Inactive) and `BillableBadge` (timesheet Billable/Non-billable)
 * both render one of these with their own semantic colors layered on via
 * `className`, so the two families stay pixel-identical in shape.
 *
 * Deliberately separate from `@/components/ui/badge` (shadcn-managed, G9 —
 * never edited): that one carries `data-slot="badge"`/`data-variant`, a
 * different padding/weight, and is meant for the app's general badges. This
 * one has neither data attribute, so it can never collide with the
 * `[data-variant='outline']` button rules in `globals.css` the way the
 * shadcn Badge's `outline` variant did.
 *
 * `w-fit shrink-0`: a badge is intrinsically sized — it should never stretch
 * to fill a wider container. `BillableBadge` sits directly inside a CSS
 * grid column (`approvals.tsx`'s `DETAIL_GRID`) with no `justify-items` set,
 * so Grid's default `stretch` was filling that whole 96px track — "Billable"
 * rendered visibly wider than its text, and wider than "Non-billable" needed
 * to be too. `inline-flex` alone doesn't opt an element out of being
 * stretched as a grid/flex *item*; `w-fit` does. The shadcn `Badge` this is
 * modeled on already carries the same pair for the same reason.
 */
const pillBadgeVariants = cva(
  'inline-flex w-fit shrink-0 items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
  {
    variants: {
      variant: {
        default: 'border-transparent bg-primary text-primary-foreground',
        secondary: 'border-transparent bg-secondary text-secondary-foreground',
        destructive: 'border-transparent bg-destructive text-destructive-foreground',
        outline: 'text-foreground',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
);

export interface PillBadgeProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof pillBadgeVariants> {}

function PillBadge({ className, variant, ...props }: PillBadgeProps) {
  return <div className={cn(pillBadgeVariants({ variant }), className)} {...props} />;
}

export { PillBadge, pillBadgeVariants };
